import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang } from "../lib/LangContext";
import { api } from "../lib/api";
import { draftStore } from "../lib/storage";

export default function WizardPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [steps, setSteps] = useState(null);
  const [answers, setAnswers] = useState({});
  const [stepIdx, setStepIdx] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const tsStartedRef = useRef(Date.now());
  const honeypotRef = useRef(null);

  // Load questionnaire whenever language changes
  useEffect(() => {
    let cancelled = false;
    api.questionnaire().catch(() => null); // no-op prefetch
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/questionnaire?lang=${lang}`);
        const d = await r.json();
        if (!cancelled) setSteps(d.steps);
      } catch (e) {
        toast.error(t("wizard.generic_error"));
      }
    })();
    return () => { cancelled = true; };
  }, [lang, t]);

  // Restore local draft on mount
  useEffect(() => {
    const draft = draftStore.load();
    if (draft && draft.answers && Object.keys(draft.answers).length > 0) {
      setAnswers(draft.answers);
      setStepIdx(Math.min(draft.stepIdx || 0, 9));
      toast.success(t("wizard.draft_restored"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist draft
  useEffect(() => {
    draftStore.save({ answers, stepIdx });
  }, [answers, stepIdx]);

  // Fetch preview when reaching the lead step
  useEffect(() => {
    if (steps && stepIdx === steps.length - 1) {
      (async () => {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/preview?lang=${lang}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answers }),
          });
          const d = await r.json();
          setPreview(d);
        } catch (_) { /* ignore */ }
      })();
    }
  }, [stepIdx, steps, answers, lang]);

  const step = steps ? steps[stepIdx] : null;
  const total = steps ? steps.length : 10;
  const progress = ((stepIdx + 1) / total) * 100;

  const setAnswer = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setErrors((e) => ({ ...e, [qid]: undefined }));
  };
  const toggleMulti = (qid, value) => {
    setAnswers((a) => {
      const arr = Array.isArray(a[qid]) ? a[qid] : [];
      return { ...a, [qid]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const validateStep = () => {
    if (!step) return true;
    const errs = {};
    for (const q of step.questions) {
      if (q.required && (answers[q.id] === undefined || answers[q.id] === "" || answers[q.id] === false)) {
        errs[q.id] = q.id === "consent" ? t("wizard.consent_required") : t("wizard.required");
      }
      if (q.kind === "email" && answers[q.id] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers[q.id])) {
        errs[q.id] = t("wizard.email_invalid");
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    if (!validateStep()) return;
    if (stepIdx < total - 1) {
      setStepIdx(stepIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      submit();
    }
  };
  const goPrev = () => {
    setErrors({});
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const lead = {
        first_name: answers.first_name || "",
        last_name: answers.last_name || "",
        email: answers.email || "",
        company: answers.company || "",
        job_title: answers.job_title || null,
        phone: answers.phone || null,
        comment: answers.comment || null,
        consent: !!answers.consent,
      };
      // Strip lead-only fields from answers passed for analysis
      const analysisAnswers = { ...answers };
      ["first_name", "last_name", "email", "company", "job_title", "phone", "comment", "consent"].forEach((k) => delete analysisAnswers[k]);

      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit?lang=${lang}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers: analysisAnswers,
          lead,
          ts_started: tsStartedRef.current,
          honeypot: honeypotRef.current?.value || "",
          app_origin: window.location.origin,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || t("wizard.generic_error"));
        setSubmitting(false);
        return;
      }
      const data = await r.json();
      draftStore.clear();
      // Stash result data for the results page (avoid refetch)
      try { sessionStorage.setItem("ariovis.lastResult", JSON.stringify({ ...data, lead, answers: analysisAnswers })); } catch (_) { /* ignore */ }
      navigate(`/resultats?token=${data.resume_token}&lang=${lang}`);
    } catch (e) {
      toast.error(t("wizard.generic_error"));
      setSubmitting(false);
    }
  };

  if (!steps) {
    return (
      <div>
        <Header variant="wizard" />
        <div className="max-w-3xl mx-auto p-8">
          <div className="animate-pulse">
            <div className="h-2 bg-slate-200 rounded-full mb-8"></div>
            <div className="h-8 bg-slate-200 rounded w-1/2 mb-6"></div>
            <div className="h-4 bg-slate-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO page="wizard" canonicalPath="/diagnostic" />
      <Header variant="wizard" />
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <div className="overline" data-testid="wizard-step-counter">{t("wizard.step_of", { n: stepIdx + 1, total })}</div>
            <div className="text-xs text-slate-500">{Math.round(progress)}%</div>
          </div>
          <div className="progress-track" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Step content */}
        <div className="reveal">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" data-testid="wizard-step-title">{step.title}</h1>
          {step.subtitle && <p className="mt-3 text-slate-600" data-testid="wizard-step-subtitle">{step.subtitle}</p>}
        </div>

        <div className="mt-10 space-y-8">
          {step.id === "lead" && preview && (
            <TeaserBlock preview={preview} />
          )}

          {step.questions.map((q) => (
            <QuestionField
              key={q.id}
              q={q}
              value={answers[q.id]}
              error={errors[q.id]}
              onChange={(v) => setAnswer(q.id, v)}
              onToggle={(v) => toggleMulti(q.id, v)}
            />
          ))}

          {/* Honeypot */}
          <div className="hidden" aria-hidden>
            <label>Do not fill: <input ref={honeypotRef} tabIndex={-1} autoComplete="off" /></label>
          </div>
        </div>

        {/* Nav */}
        <div className="mt-12 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={goPrev}
            disabled={stepIdx === 0 || submitting}
            className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="wizard-prev"
          >
            <ArrowLeft size={16} /> {t("wizard.previous")}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={submitting}
            className="btn-primary disabled:opacity-70"
            data-testid={stepIdx === total - 1 ? "wizard-submit" : "wizard-next"}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> {t("wizard.saving")}
              </>
            ) : stepIdx === total - 1 ? (
              <>{t("wizard.finish")} <ArrowRight size={16} /></>
            ) : (
              <>{t("wizard.next")} <ArrowRight size={16} /></>
            )}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function TeaserBlock({ preview }) {
  const { t, lang } = useLang();
  const iagPresent = preview.ctas_ids?.includes("iag");
  return (
    <div className="card-ariovis border-slate-900" data-testid="teaser-block">
      <div className="overline">{t("teaser.title")}</div>
      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
          <div className="text-xs text-slate-500 uppercase tracking-wider">{t("teaser.current_level")}</div>
          <div className="mt-2 text-3xl font-bold text-slate-900" data-testid="teaser-current-level">
            {lang === "en" ? "Level" : "Niveau"} {preview.current_level}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
          <div className="text-xs text-slate-500 uppercase tracking-wider">{t("teaser.priorities_count")}</div>
          <div className="mt-2 text-3xl font-bold text-slate-900" data-testid="teaser-priorities-count">{preview.gaps_count}</div>
        </div>
        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 relative overflow-hidden">
          <div className="text-xs text-slate-500 uppercase tracking-wider">{t("teaser.target_hidden")}</div>
          <div className="mt-2 text-3xl font-bold text-slate-900 select-none blur-[6px]">Niveau {preview.target_level}</div>
        </div>
      </div>
      <p className="mt-4 text-sm text-slate-600">{t("teaser.text")}</p>
    </div>
  );
}

function QuestionField({ q, value, error, onChange, onToggle }) {
  const { t } = useLang();
  const label = q.label;
  const required = q.required;
  const kind = q.kind || "single";

  if (kind === "checkbox") {
    return (
      <div className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg bg-slate-50" data-testid={`q-${q.id}`}>
        <input
          type="checkbox"
          id={q.id}
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 w-4 h-4"
          data-testid={`input-${q.id}`}
        />
        <label htmlFor={q.id} className="text-sm text-slate-700 leading-relaxed cursor-pointer">
          {label} {required && <span className="text-fuschia" style={{ color: "#DE0F72" }}>*</span>}
        </label>
        {error && <div className="text-xs text-red-600">{error}</div>}
      </div>
    );
  }

  return (
    <fieldset className="reveal" data-testid={`q-${q.id}`}>
      <legend className="text-base font-semibold text-slate-900">
        {label} {required && <span style={{ color: "#DE0F72" }}>*</span>}
        {!required && q.kind !== "single" && q.kind !== "multi" && <span className="ml-2 text-xs text-slate-400">({t("wizard.optional")})</span>}
      </legend>
      {q.help && <div className="mt-1 text-sm text-slate-500 flex items-start gap-1"><HelpCircle size={14} className="mt-0.5" />{q.help}</div>}

      <div className="mt-3">
        {kind === "single" && (
          <div className="grid sm:grid-cols-2 gap-2">
            {q.options.map((o) => (
              <label key={o.value} className={`cursor-pointer border rounded-lg px-4 py-3 text-sm transition-colors ${value === o.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400 bg-white text-slate-700"}`} data-testid={`opt-${q.id}-${o.value}`}>
                <input type="radio" name={q.id} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} className="sr-only" />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        )}

        {kind === "multi" && (
          <div className="grid sm:grid-cols-2 gap-2">
            {q.options.map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const on = arr.includes(o.value);
              return (
                <button key={o.value} type="button" onClick={() => onToggle(o.value)} className={`text-left border rounded-lg px-4 py-3 text-sm transition-colors ${on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400 bg-white text-slate-700"}`} data-testid={`opt-${q.id}-${o.value}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        )}

        {kind === "select" && (
          <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-slate-900" data-testid={`input-${q.id}`}>
            <option value="">—</option>
            {q.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        {(kind === "text" || kind === "email") && (
          <input
            type={kind === "email" ? "email" : "text"}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={q.placeholder || ""}
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-slate-900"
            data-testid={`input-${q.id}`}
          />
        )}

        {kind === "textarea" && (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            placeholder={q.placeholder || ""}
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-slate-900"
            data-testid={`input-${q.id}`}
          />
        )}
      </div>

      {error && <div className="mt-2 text-xs text-red-600" data-testid={`error-${q.id}`}>{error}</div>}
    </fieldset>
  );
}
